import { DataTypes, SchemaProperty, UOMS, UomTypes } from '@amzn/awscat-portfolio-assessment-types';
import React from 'react';

import { constants } from '../../common/Localization';
import { ColumnDefinition, ColumnTypes, Query, Token } from '../../common/table/types';
import { uomConversion } from '../../common/uomConversion';
import { columnOrder, ColumnOrderType } from './constants';
import { getInlineEditCell } from './inline-editing/InlineEditUtils';
import { SchemaPropertyMap } from './types';

export function generateColumnDefs<T>(
    items: T[],
    headers: SchemaPropertyMap,
    typeToOrder: ColumnOrderType,
    columnsToExclude?: string[],
    isInlineEdit?: boolean,
): ColumnDefinition<T>[] {
    if (!items || !items.length) {
        return [];
    }
    const visibleColumns: {
        [column: string]: ColumnDefinition<T>;
    } = {};

    items.forEach(item => {
        for (const prop in headers) {
            if (columnsToExclude && columnsToExclude.includes(prop)) {
                continue;
            }

            const schemaProp = headers[prop];
            const itemValue = (item as any)[prop];
            if (!schemaProp || itemValue === undefined || itemValue === null || itemValue === '') {
                continue;
            }
            if (!visibleColumns[prop]) {
                const columnDefinition = {
                    id: schemaProp.value,
                    sortingField: schemaProp.value,
                    header: schemaProp.text,
                    text: schemaProp.text,
                    type: getColumnType(schemaProp.dataType),
                    data: getColumnData(schemaProp, prop),
                    cell: isInlineEdit ? getInlineEditCell(schemaProp, prop) : getColumnCell(schemaProp, prop),
                } as ColumnDefinition<T>;
                visibleColumns[prop] = columnDefinition;
            }
        }
    });

    // Add flexible schema to visibleColumns even if no value exists
    Object.keys(headers).forEach(prop => {
        if (headers[prop].mutable && !visibleColumns[prop]) {
            const schemaProp = headers[prop];
            const columnDefinition = {
                id: schemaProp.value,
                sortingField: schemaProp.value,
                header: schemaProp.text,
                text: schemaProp.text,
                type: getColumnType(schemaProp.dataType),
                data: getColumnData(schemaProp, prop),
                cell: isInlineEdit ? getInlineEditCell(schemaProp, prop) : getColumnCell(schemaProp, prop)
            } as ColumnDefinition<T>;
            visibleColumns[prop] = columnDefinition;
        }
    });

    const visibleColumnDefinitions = Object.values(visibleColumns);
    const orderedVisibleColumns = orderColumns(typeToOrder, visibleColumnDefinitions);

    return orderedVisibleColumns;
};

function getColumnType(dataType?: string): ColumnTypes {
    if (dataType === 'number') {
        return ColumnTypes.NUMBER;
    }

    if (dataType === 'boolean') {
        return ColumnTypes.BOOLEAN;
    }

    return ColumnTypes.TEXT;
}

function getColumnCell(schemaProp: SchemaProperty, columnValue: string): (item: any) => JSX.Element | undefined {
    if (schemaProp.dataType === DataTypes.APPLICATION_SELECT || schemaProp.type === DataTypes.APPLICATION_SELECT) {
        return (item: any) => ['string', 'number'].includes(typeof(item)) ? 
            undefined : 
            React.createElement('a', { href: schemaProp.onClickRedirect?.(item) }, item[columnValue]);
    }

    if (schemaProp.dataType === DataTypes.BOOLEAN || schemaProp.type === 'boolean') {
        return (item: any) => ['string', 'number', 'boolean'].includes(typeof(item)) ? 
            undefined : 
            React.createElement('div', null, item[columnValue] ? constants.TRUE : constants.FALSE);
    }

    if (schemaProp.UOM === UomTypes.FREQUENCY) {
        return (item: any) => ['string', 'number'].includes(typeof(item)) ? undefined : React.createElement('div', null, (item[columnValue] ?
            Number(item[columnValue]).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' GHz' : ''));
    }

    if (schemaProp.UOM === UomTypes.STORAGE) {
        return (item: any) => ['string', 'number'].includes(typeof(item)) ? undefined :  React.createElement('div', null, (item[columnValue] ?
            Number(item[columnValue]).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' KB' : ''));
    }

    if (schemaProp.UOM === UomTypes.PERCENTAGE) {
        return (item: any) => {
            if (['string', 'number'].includes(typeof(item))){
                return undefined;
            } else {
                const content = item[columnValue] ?
                    uomConversion(item[columnValue], schemaProp.dbUOM || UOMS.DECIMAL, schemaProp.webUOM || UOMS.PERCENTAGE).toFixed(2) + ' %' : '';
                return React.createElement('div', null, content);
            }
        };
    }

    return (item: any) => ['string', 'number'].includes(typeof(item)) ? undefined : React.createElement('div', null, item[columnValue]);
}

/**
 * Data function determines filter values shown to the user and data displayed in the downloaded file. 
 * Change percentages so they are in the same format as UI side.
 * @param schemaProp 
 * @param columnValue 
 * @returns 
 */
export function getColumnData<T>(schemaProp: SchemaProperty, columnValue: string): (item: T) => string | number {
    if (schemaProp.UOM === UomTypes.PERCENTAGE) {
        return (item: T) => item[columnValue as keyof T] ? uomConversion(item[columnValue as keyof T] as any as string | number, 
            schemaProp.dbUOM || UOMS.DECIMAL, schemaProp.webUOM || UOMS.PERCENTAGE).toFixed(2) + '%' : '';
    } else {
        return (item: T) => item[columnValue as keyof T] as any as string | number ?? '';
    }
}

/**
 * Orders columns by predefined list, with wildcard spot for any columns not specified
 * @param type gets the correct list of columns
 * @param columns column definitions to order
 * @returns the ordered column definition array
 */
const orderColumns = <T>(type: ColumnOrderType, columns: ColumnDefinition<T>[]): ColumnDefinition<T>[] => {
    const columnTypeOrder = columnOrder[type];
    const result = columns.sort( (left, right) => {
        const l = left.id;
        const r = right.id;
        const leftIndex = columnTypeOrder.indexOf(l) + 1 || columnTypeOrder.indexOf('*') + 1;
        const rightIndex = columnTypeOrder.indexOf(r) + 1 || columnTypeOrder.indexOf('*') + 1;
        return (leftIndex - rightIndex) || (l === r ? 0 : (l < r ? -1 : 1));
    });
    return result;
};

/**
 * Generate Camel case string from the input string
 * @param input 
 * @returns string in camel case
 */
export const toCamelCase = (input: string): string => {
    if (input.length > 0) {
        const words = toWords(input);
        return wordsToCamelCase(words);
    } else {
        return input;
    }
};

const toWords = (input: string): RegExpMatchArray | null => {
    const seperateWordRegex = /[A-Z]?[a-z]+|[A-Z]+(?![a-z])|\d+/g;
    return input.match(seperateWordRegex);
};

const wordsToCamelCase = (words: RegExpMatchArray | null): string => {
    if (words === null) {
        return '';
    }
    return words.map((word: string, index) => {
        let tempWord = word.toLocaleLowerCase();
        if (index !== 0) {
            tempWord = tempWord.substr(0, 1).toUpperCase() + tempWord.substr(1);
        }
        return tempWord;
    }).join('');
};

/**
 * Converts a camel case string into space separated capitalized words
 * @param input 
 * @returns 
 */
export const camelCaseToTitleCase = (input: string): string => {
    const result = input.replace(/([A-Z])/g, ' $1');
    const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
    return finalResult;
};

/**
 * Capitalizes one word
 * @param input 
 * @returns 
 */
export const capitalizeWord = (input: string): string => {
    input = input.toLowerCase();
    return input.charAt(0).toUpperCase() + input.slice(1);
};

export const getFilterQuery = (invalidIds: string[], filterColumnId: string): Query => { 
    const tokens: Token[] = invalidIds.map(invalidId => {
        const token: Token = {
            operator: '=',
            value: invalidId,
            propertyKey: filterColumnId
        };
        return token;
    });
    return {
        tokens: tokens,
        operation: 'or'
    };
};
export interface UOMOption {
    value: keyof typeof UOMS;
    label: UOMS;
}

/**
 * Generate UOM selection options from uom types
 * @param uom uom types of schema property
 * @returns 
 */
export const generateUomOptions = (uom?: UomTypes): UOMOption[] => {
    if (uom === UomTypes.FREQUENCY) {
        return [{
            value: 'GHz',
            label: UOMS.GHz,
        }, {
            value: 'MHz',
            label: UOMS.MHz,
        }, {
            value: 'KHz',
            label: UOMS.KHz,
        }, {
            value: 'Hz',
            label: UOMS.Hz
        }];
    } else if (uom === UomTypes.STORAGE) {
        return [{
            value: 'TB',
            label: UOMS.TB,
        }, {
            value: 'GB',
            label: UOMS.GB,
        }, {
            value: 'MB',
            label: UOMS.MB,
        }, {
            value: 'KB',
            label: UOMS.KB,
        }, {
            value: 'BYTE',
            label: UOMS.BYTE,
        }];
    } else if (uom === UomTypes.PERCENTAGE) {
        return [{
            value: 'DECIMAL',
            label: UOMS.DECIMAL,
        }, {
            value: 'PERCENTAGE',
            label: UOMS.PERCENTAGE,
        }];
    }
    return [];
};
